<template>
  <div class="navigation-item">
    <ElPopover
      placement="left-start"
      trigger="hover"
      popper-class="navigation-item__popover"
      :disabled="!sidebarCollapsed || disabled"
      :close-delay="0"
    >
      <NavigationItemLink
        slot="reference"
        :title="title"
        :route="route"
        :icon="icon"
        :has-child="hasChild"
        :show-badge="hasBadge"
        :new-badge="newBadge"
        :warn-badge="warnBadge"
        :badge-count="badgeCount"
        :left-icon="arrowIconClass"
        :disabled="disabled"
        @click="handleItemClick"
      />

      <NavigationItemPopover :title="title" :items="availableChildren" />
    </ElPopover>

    <ElCollapseTransition>
      <NavigationSubItems
        v-if="!sidebarCollapsed && isOpen"
        :items="availableChildren"
        class="navigation-item__sub-menu"
      />
    </ElCollapseTransition>
  </div>
</template>

<script>
import NavigationSubItems from './NavigationSubItems';
import NavigationItemLink from './NavigationItemLink';
import NavigationItemPopover from './NavigationItemPopover';
import { SIDENAV_COLLAPSED } from '@/store/types';
import { mapGetters } from 'vuex';

export default {
  name: 'NavigationItem',

  components: {
    NavigationSubItems,
    NavigationItemLink,
    NavigationItemPopover,
  },

  props: {
    title: {
      type: String,
      required: true,
    },
    route: {
      type: String,
      required: true,
    },
    icon: {
      type: Object,
      default: null,
    },
    newBadge: {
      type: Boolean,
      default: false,
    },
    warnBadge: {
      type: Boolean,
      default: false,
    },
    badgeCount: {
      type: Number,
      default: 0,
    },
    children: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isOpen: false,
    };
  },

  computed: {
    ...mapGetters({ sidebarCollapsed: SIDENAV_COLLAPSED }),

    availableChildren() {
      return this.children.filter(item => {
        const validAuth = !item.auth || this.$auth.check(item.auth);
        const isEnabled = item.enabled !== false;

        return validAuth && isEnabled;
      });
    },
    hasChild() {
      return Boolean(this.availableChildren?.length);
    },
    arrowIconClass() {
      if (!this.hasChild) return '';
      return this.isOpen ? 'el-icon-arrow-up' : 'el-icon-arrow-down';
    },
    hasBadge() {
      const hasChildBadge = this.availableChildren.some(
        ch => ch.newBadge || ch.warnBadge || Number(ch.badgeCount) > 0,
      );

      return hasChildBadge || (this.sidebarCollapsed && this.warnBadge);
    },
  },

  created() {
    this.openSubMenuIfIsActive();
  },

  methods: {
    handleItemClick(event) {
      event.preventDefault();
      if (this.disabled) return;
      if (this.hasChild) {
        this.isOpen = !this.isOpen;
      } else {
        this.$router.push({ name: this.route }).catch(() => {});
      }
    },
    openSubMenuIfIsActive() {
      if (this.disabled) return;
      const shouldOpen = this.availableChildren.some(
        ch => ch.route === this.$route.name,
      );

      if (shouldOpen) this.isOpen = true;
    },
  },
};
</script>

<style lang="scss">
.navigation-item {
  &__sub-menu {
    border-left: 4px solid #eee;
    margin-left: $global-margin * 1.7;

    @include rtl() {
      margin-left: 0;
      border-left: 0;
      border-right: 4px solid #eee;
      margin-right: $global-margin * 1.7;
    }
  }

  &__popover {
    min-width: 0 !important;
    transform: translateX(-10px);

    @include rtl() {
      transform: translateX(10px);
    }
  }
}
</style>
